/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import EventEmitter from 'eventemitter3';

/**
 * @import { ControlButtonSequence, PartialControlButtonSequence, StatusInfoDialogOpenStrategyEventTypes, StatusInfoDialogOpenStrategy } from '..'
 * @import ControlButton from '../../control/control-button'
 * @import { ControlListener } from '../../control/control-listener'
 */

/**
 * Časové okno [ms], během kterého musí být stisknuto
 * požadované množství tlačítek. Pokud jsou stisky tlačítek mimo toto
 * časové okno, nebudou započítány.
 */
const pressSeqTimeWindow = 1000;

/**
 * Třída pro strategii otevření dialogu na základě specifické sekvence stisknutí tlačítek.
 *
 * Konkrétně implementuje způsob, kdy je dialog otevřen po splnění podmínky,
 * že uživatel stiskne tlačítka během definovaného časového okna. Pokud
 * jsou stisky tlačítek mimo toto časové okno, nejsou započítány.
 *
 * Restrikce:
 * - Časové okno je definováno konstantou {@link pressSeqTimeWindow} (v milisekundách).
 * @implements {StatusInfoDialogOpenStrategy}
 */
export default class StatusInfoDialogOpenStrategySequence {
  /**
   * @param {ControlListener} controlListener
   * @param {Object} [opts]
   * @param {ControlButtonSequence} [opts.sequence]
   */
  constructor(controlListener, { sequence = '9966' } = {}) {
    /** @type {EventEmitter<StatusInfoDialogOpenStrategyEventTypes>} */
    this.emitter = new EventEmitter();
    this.controlListener = controlListener;
    this.sequence = sequence;
    /** @type {PartialControlButtonSequence} */
    this.activeSequence = '';
    /** @type {number | null} */
    this.pressStartTimestamp = null;
    /** @type {() => void} */
    this.onButtonDown = this.onButtonDown.bind(this);
    this.controlListener.on('buttonDown', this.onButtonDown);
  }

  /** @type {StatusInfoDialogOpenStrategy['destroy']} */
  destroy() {
    this.controlListener.off('buttonDown', this.onButtonDown);
  }

  /** @type {StatusInfoDialogOpenStrategy['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {StatusInfoDialogOpenStrategy['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * @private
   * @param {ControlButton} controlButton
   */
  onButtonDown(controlButton) {
    // Otevírání dialogu se ignoruje, pokud je aktivní vstupní nebo textové pole.
    if (
      document.activeElement?.tagName === 'INPUT' ||
      document.activeElement?.tagName === 'TEXTAREA'
    ) {
      return;
    }
    const now = Date.now();
    if (
      this.pressStartTimestamp &&
      now - this.pressStartTimestamp > pressSeqTimeWindow
    ) {
      this.resetSequence();
    }
    if (this.pressStartTimestamp === null) {
      this.pressStartTimestamp = now;
    }
    this.activeSequence += controlButton;
    if (this.activeSequence === this.sequence) {
      this.emitter.emit('open');
      this.resetSequence();
    }
  }

  /** @private */
  resetSequence() {
    this.activeSequence = '';
    this.pressStartTimestamp = null;
  }
}
